/**
 * @module services/resolveVideoUrl
 *
 * Resolves a /play/:videoId.:ext server URL to a presigned R2 URL for direct
 * playback, bypassing the server redirect. Extracted out of MediaPlayer.vue
 * so the standalone quick-play dialog can reuse the exact same behavior.
 */

import { api } from "@/api/config.js";

/**
 * Calls the server's /play/:videoId.:ext endpoint with ?url=true, which
 * returns a JSON response { success: true, url: "https://r2..." } instead of
 * issuing a 302 redirect.
 *
 * Falls back to the original server URL if:
 *   - The server returns a response without a URL (e.g., the file is still
 *     downloading and hasn't been uploaded to R2 yet)
 *   - The request fails entirely (network error, 404, etc.)
 *
 * @param {string} serverUrl - The full /play/:videoId.:ext URL (e.g., "https://api.host/play/abc123.mp4")
 * @returns {Promise<string>} The resolved presigned R2 URL, or the original serverUrl as fallback
 */
export const resolveVideoUrl = async (serverUrl) => {
  try {
    // Extract just the path portion from the full URL, because the Axios api
    // client already has baseURL configured. We strip the base to avoid
    // a double-prefixed URL like "https://host/https://host/play/..."
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    const path = serverUrl.startsWith(baseUrl)
      ? serverUrl.slice(baseUrl.length)
      : serverUrl;

    // Append ?url=true query parameter. If the URL already has query params
    // (unlikely but possible), use & instead of ? as the separator.
    const separator = path.includes("?") ? "&" : "?";
    const { data } = await api.get(`${path}${separator}url=true`);

    // Check for a valid response with a presigned URL
    if (data?.success && data?.url) {
      return data.url;
    }

    // Server responded successfully but without a URL — this happens when the
    // file is not yet cached (download in progress or just started). The video
    // element will use the original URL which triggers the download on the server.
    return serverUrl;
  } catch (err) {
    // URL resolution failed (e.g., 404, network timeout, server down).
    // Fall back to the original server URL so the video element can still
    // attempt to load, which may trigger the download process on the server.
    console.warn("Failed to resolve video URL, using direct:", err.message);
    return serverUrl;
  }
};
